'use client'
import { type FC } from 'react'
import { Box, Button, HStack, VStack } from '@chakra-ui/react'
import { BiCart } from 'react-icons/bi'
import { GrandText } from 'ui'
import { overrideText } from 'ui/lib/overrides'
import { useOverridesContext } from 'ui/lib/overrides/hooks'
import { useCheckoutContext } from '../../../hooks'
import { InvoiceAndIngrid } from './InvoiceAndIngrid'
import { InvoiceAndNshift } from './InvoiceAndNshift'
import { InvoiceAndPostnord } from './InvoiceAndPostnord'
import { InvoiceDefault } from './InvoiceDefault'

interface CheckoutInvoicePaymentViewProps {
  /** /api route mapped to PUT /storefront/invoice (flips DRAFT → CREATED). */
  submitPath: string
}

/**
 * Top-level invoice payment view. Mirrors `CheckoutPaymentView` — the carrier
 * matrix is identical (DEFAULT / INGRID / POSTNORD / NSHIFT) but the right-hand
 * side renders an invoice review panel instead of a payment-provider widget.
 *
 * Layout: the "back to cart" affordance stays pinned to the left (matches
 * the upfront flow), while the content column is horizontally centred with a
 * comfortable reading width — the invoice flow has no carrier widget pulling
 * the right column, so a single centred column reads cleaner than a
 * left-anchored one.
 *
 * Locale-aware default copy is supplied by the `OverridesContextProvider` in
 * `CheckoutPageStd` (so the order-mode switch on the summary page is covered
 * too); brand-level overrides set higher up still win.
 */
export const CheckoutInvoicePaymentView: FC<CheckoutInvoicePaymentViewProps> = ({ submitPath }) => {
  const { shippingConfig, handleResetOrder } = useCheckoutContext()
  const overrides = useOverridesContext<'invoiceReviewBackToCart'>()

  const renderContent = () => {
    switch (shippingConfig?.provider ?? 'DEFAULT') {
      case 'INGRID':
        return <InvoiceAndIngrid submitPath={submitPath} />
      case 'POSTNORD':
        return <InvoiceAndPostnord submitPath={submitPath} />
      case 'NSHIFT':
        return <InvoiceAndNshift submitPath={submitPath} />
      case 'DEFAULT':
      default:
        return <InvoiceDefault submitPath={submitPath} />
    }
  }

  return (
    <VStack align="stretch" gap={6} w="full">
      <Button alignSelf="flex-start" aria-label="back-to-cart" onClick={handleResetOrder} size="sm">
        <HStack spacing={2}>
          <BiCart size={22} />
          <GrandText>
            {overrideText('Tillbaka till varukorgen', overrides?.invoiceReviewBackToCart)}
          </GrandText>
        </HStack>
      </Button>

      <Box maxW="720px" mx="auto" w="full">
        {renderContent()}
      </Box>
    </VStack>
  )
}

export default CheckoutInvoicePaymentView
